"use strict";
/*
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCriteriaWeights = exports.getSearchResultDescription = exports.getLotTitle = exports.generateLotTitleWithNumber = exports.getNoticeTitle = exports.cleanTitle = void 0;
const uuid_1 = require("uuid");
const DEFAULT_MAX_CHARS = 155;
/**
 * Removes escape characters from string.
 * @param text: string - the text to be cleaned.
 * */
const __removeEscapeCharacters = (text) => {
    const refinedText = text.replace(/[\\"\\|]/g, '');
    /* You can add additional escape characters as needed */
    return refinedText;
};
/**
 * Gets the translation text
 * @param translationKey - Key of i18n translation on UI
 * @param translatedText - Default translated text
 * @param isTranslated - Boolean to return a translation key or translated text
 * @returns - String
 */
const __getTranslatedText = (translationKey, translatedText, isTranslated) => {
    return isTranslated ? translatedText : translationKey;
};
/**
 * Cleanses any strings passed
 * @param title - Any string
 * @returns String
 */
const cleanTitle = (title, translatedText, isTranslated, maxChars) => {
    if (title !== 'missingTitle' && title !== 'missingDescription') {
        const removedEscapedCharacters = __removeEscapeCharacters(title);
        const cleanedTitle = `${removedEscapedCharacters.charAt(0).toUpperCase()}${removedEscapedCharacters.slice(1)}`;
        return cleanedTitle.substring(0, maxChars);
    }
    return __getTranslatedText(title, translatedText, isTranslated);
};
exports.cleanTitle = cleanTitle;
/**
 * Gets the title of flat lot or entity
 * @param searchResultUnion
 * @param isTranslated - Whether you need the translated text of missing title (On backend)
 * @returns clean title
 */
const getNoticeTitle = (searchResultUnion, isTranslated = false, maxChars = DEFAULT_MAX_CHARS) => {
    const title = searchResultUnion.contractTitle || searchResultUnion.title || searchResultUnion.lotTitle || 'missingTitle';
    return (0, exports.cleanTitle)(title, 'Missing Title', isTranslated, maxChars);
};
exports.getNoticeTitle = getNoticeTitle;
const generateLotTitleWithNumber = (title, lotNumber) => {
    // Removing the Number from the title
    let newTitle = title.replace(/^\d+\./g, '');
    if (lotNumber) {
        if (lotNumber.indexOf('Lot') > -1) {
            newTitle = `${lotNumber} - ${newTitle}`;
        }
        else {
            newTitle = `Lot ${lotNumber} - ${newTitle}`;
        }
    }
    return newTitle;
};
exports.generateLotTitleWithNumber = generateLotTitleWithNumber;
const getLotTitle = (lotDetails, isTranslated = false, maxChars = DEFAULT_MAX_CHARS) => {
    const { lotTitle, lotNumber } = lotDetails;
    let title = lotTitle || 'missingTitle';
    title = (0, exports.cleanTitle)(title, 'Missing Title', isTranslated, maxChars);
    title = (0, exports.generateLotTitleWithNumber)(title, lotNumber);
    return title;
};
exports.getLotTitle = getLotTitle;
/**
 * Gets the description of flat lot or entity
 * @param searchResultUnion
 * @param isTranslated - Whether you need the translated text of missing description (On backend)
 * @returns cleaned description
 */
const getSearchResultDescription = (searchResultUnion, isTranslated = false, maxChars = DEFAULT_MAX_CHARS) => {
    const description = searchResultUnion.contractDescriptionShort ||
        searchResultUnion.contractDescription ||
        searchResultUnion.lotProcurementDescription ||
        'missingDescription';
    return (0, exports.cleanTitle)(description, 'Missing Description', isTranslated, maxChars);
};
exports.getSearchResultDescription = getSearchResultDescription;
/**
 * Joins weight based on index with criteria name
 * @param criteria - All the criteria names
 * @param weights - All the criteria weights
 * @param isPriceCriteria - Whether it's quality or price criteria
 * @returns - IEmCriteriaWeight[]
 */
const joinWeightsByIndex = (criteria, weights, isPriceCriteria) => {
    const criteriaWeights = [];
    for (let i = 0; i < weights.length; i += 1) {
        if (criteria[i]) {
            criteriaWeights.push({
                id: (0, uuid_1.v4)(),
                name: criteria[i].trim(),
                finalWeight: weights[i],
                suggestedWeight: weights[i],
                required: false,
                type: isPriceCriteria ? 'price' : 'quality',
            });
        }
    }
    return criteriaWeights;
};
const parseToNumber = (parsedWeight) => {
    const numbers = parsedWeight.match(/\d+/);
    let finalWeight = 0;
    if (numbers && numbers.length > 0) {
        numbers.forEach(num => {
            const weight = parseFloat(num);
            if (!Number.isNaN(weight)) {
                finalWeight = Number(weight);
            }
        });
    }
    return finalWeight;
};
/**
 * Cleanses the criteria weight from string to number
 * @param trimmedWeight - Clean and trimmed weight
 * @returns - Weight which is number
 */
const cleanCriteriaWeight = (trimmedWeight) => {
    let finalWeight = 0;
    if (trimmedWeight.includes('%')) {
        const parsedWeight = trimmedWeight.split('%');
        if (parsedWeight.length > 0) {
            finalWeight = parseToNumber(parsedWeight[0]);
        }
    }
    else {
        const weight = parseFloat(trimmedWeight);
        if (Number.isNaN(weight)) {
            finalWeight = parseToNumber(trimmedWeight);
        }
        else {
            finalWeight = Number(weight);
        }
    }
    return finalWeight;
};
/**
 * Parses the weights
 * @param weights - Weights of the tender passes
 * @returns - Number array
 */
const parseWeights = (weights) => {
    let weightNum = [];
    if (typeof weights === 'string') {
        weightNum = weights.split(/[|/]/).map(val => cleanCriteriaWeight(val.trim()));
    }
    else if (typeof weights === 'number') {
        weightNum = [weights];
    }
    return weightNum;
};
/**
 * Parses the criteria names
 * @param criteriaName - Character separated criteria names
 * @returns - Array of criteria names
 */
const parseCriteriaNames = (criteriaName) => {
    let criteria = [''];
    if (typeof criteriaName === 'string') {
        criteria = criteriaName.split(/[|/]/).map(cn => cn.trim());
    }
    return criteria;
};
/**
 * Gets the criteria weights from the public tender - Parses string to criteria weights object
 * @param charSeparatedCriteria - Pipe separated criteria names
 * @param charSeparatedWeights - Pipe separated criteria weights
 * @param isPriceCriteria - Whether it's quality or price criteria
 * @returns - IEmCriteriaWeight[]
 */
const getCriteriaWeights = (charSeparatedCriteria, charSeparatedWeights, isPriceCriteria) => {
    let criteriaWeights = [];
    if (charSeparatedWeights) {
        const criteria = parseCriteriaNames(charSeparatedCriteria);
        const weights = parseWeights(charSeparatedWeights);
        if (criteria.length === weights.length) {
            criteriaWeights = joinWeightsByIndex(criteria, weights, isPriceCriteria);
        }
    }
    return criteriaWeights;
};
exports.getCriteriaWeights = getCriteriaWeights;
//# sourceMappingURL=data:application/json;base64,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